import classnames from 'classnames';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconPen } from '@sorare/core/src/atoms/icons/system/iconPen.ts';
import { iconPlus } from '@sorare/core/src/atoms/icons/system/iconPlus.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { FilterSearchInput } from '@sorare/core/src/components/search/FilterSearchInput';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { Option } from '@sorare/core/src/components/search/Option';
import { useFacet } from '@sorare/core/src/contexts/graphql-search';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards';
import { useDecks } from '@sorare/core/src/hooks/decks/useDecks';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { filters, glossary } from '@sorare/core/src/lib/glossary';

import { DeckEditorDialog } from '#src/components/decks/DeckEditorDialog/index.tsx';

const FILTER = VIRTUAL_TOGGLE_FILTERS.customDecksFilter;
const BACKEND_DECK_KEY = 'deck';

const DecksContainer = styled.div`
  position: relative;
  max-height: calc(20 * var(--unit));
  overflow: hidden;
  &.expanded {
    max-height: none;
  }
`;
const ShowMore = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  color: var(--c-white);
  background-color: var(--c-black);
`;
const Search = styled.div`
  display: grid;
  gap: var(--unit);
  grid-template-columns: 1fr min-content;
  width: 100%;
`;
const AfterOptionWrapper = styled(Horizontal)`
  margin-left: auto;
`;

const AfterOption = ({ onEdit }: { onEdit: () => void }) => {
  const { formatMessage } = useIntl();
  return (
    <AfterOptionWrapper as="span">
      <IconButton
        icon={iconPen}
        onClick={onEdit}
        size="small"
        aria-label={formatMessage(glossary.edit)}
        color="transparent"
      />
    </AfterOptionWrapper>
  );
};

const BackendFilter = ({ visible }: { visible: boolean }) => {
  const { formatMessage } = useIntl();
  const { editableLists, galleryOwnerSlug } = useSearchCardsContext();
  const [search, setSearch] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [editingDeckSlug, setEditingDeckSlug] = useState<string>();
  const [expanded, setExpanded] = useState(false);

  const { decks, loading } = useDecks({
    sport: Sport.BASEBALL,
    query: search,
    userSlug: editableLists ? undefined : galleryOwnerSlug,
  });

  const { value, refine } = useFacet(BACKEND_DECK_KEY);
  const currentRefinement = value[0];
  const { value: favoriteValue, refine: refineFavorite } = useFacet('ff');
  const favoriteActive = !!favoriteValue.length;

  const onChange = useCallback(
    (deckName: string) => {
      const isActive = currentRefinement === deckName;
      refine(() => (isActive ? [] : [deckName]));
    },
    [currentRefinement, refine]
  );

  const onToggleFavorite = useCallback(() => {
    refineFavorite(() => (favoriteActive ? [] : ['true']));
  }, [favoriteActive, refineFavorite]);

  const displayShowMore = !expanded && decks.length > 5;

  if (!visible) return <FilterSection />;

  return (
    <>
      <FilterSection
        search={
          <Search>
            <FilterSearchInput
              handleChange={e => {
                setSearch(e.target.value);
              }}
              value={search}
            />
            {!!editableLists && (
              <IconButton
                icon={iconPlus}
                onClick={() => setCreating(true)}
                color="tertiary"
                size="small"
                aria-label={formatMessage({
                  defaultMessage: 'Create new list',
                })}
              />
            )}
          </Search>
        }
      >
        <DecksContainer className={classnames({ expanded: !displayShowMore })}>
          {!!loading && !decks.length && <LoadingIndicator small />}
          <Option
            variant="checkbox"
            active={favoriteActive}
            onClick={onToggleFavorite}
          >
            {formatMessage(filters.favoriteFilterLabel)}
          </Option>
          {decks.map(deck => {
            const { name, slug, tokensCount } = deck;
            return (
              <Option
                key={slug}
                active={currentRefinement === name}
                onClick={() => onChange(name)}
                variant="checkbox"
                count={tokensCount}
                afterLabel={
                  !!editableLists && (
                    <AfterOption onEdit={() => setEditingDeckSlug(slug)} />
                  )
                }
              >
                {name}
              </Option>
            );
          })}
          {!!displayShowMore && (
            <ShowMore>
              <Button
                size="small"
                fullWidth
                color="transparent"
                onClick={() => setExpanded(true)}
              >
                <FormattedMessage defaultMessage="Show more" />
              </Button>
            </ShowMore>
          )}
        </DecksContainer>
      </FilterSection>
      {!!(editingDeckSlug || creating) && (
        <DeckEditorDialog
          sport={Sport.BASEBALL}
          slug={editingDeckSlug}
          onClose={() => {
            setEditingDeckSlug(undefined);
            setCreating(false);
          }}
        />
      )}
    </>
  );
};

const BackendTitle = () => {
  const { value } = useFacet(BACKEND_DECK_KEY);
  return <FilterTitle name={FILTER.title!} selected={!!value.length} />;
};

export const backendWidget: FilterWidget = {
  key: `${FILTER.name}_backend`,
  type: 'virtualToggle',
  filter: FILTER,
  Component: ({ visible }) => <BackendFilter visible={visible} />,
  title: <BackendTitle />,
  accordionOptions: { startsOpen: true },
};
